import { useEffect, useState } from "react";
import {
    useNavigate,
    useSearchParams,
} from "react-router-dom";

import {
    doc,
    getDoc,
    updateDoc,
    serverTimestamp,
    deleteDoc,
} from "firebase/firestore";

import { db } from "../firebase/firebase";

export default function BlogView() {
    const navigate = useNavigate();

    const [searchParams] =
        useSearchParams();

    const id = searchParams.get("id");

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [activeTab, setActiveTab] =
        useState("overview");

    const [blog, setBlog] = useState({
        title: "",
        slug: "",
        description: "",
        content: "",
        category: "",
        image: "",
        gallery: [],
        metaTitle: "",
        metaDescription: "",
        tags: [],
        author: "SkillAura",
        status: "published",
        featured: false,
        ctaText: "",
        ctaLink: "",
        views: 0,
        likes: 0,
        readingTime: 0,
    });

    useEffect(() => {
        fetchBlog();
    }, []);

    const fetchBlog = async () => {
        try {
            const snap = await getDoc(
                doc(db, "blogs", id)
            );

            if (snap.exists()) {
                setBlog(snap.data());
            } else {
                alert("Blog not found");
                navigate(-1);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            await updateDoc(
                doc(db, "blogs", id),
                {
                    ...blog,
                    updatedAt:
                        serverTimestamp(),
                }
            );

            alert("Blog Updated ✅");
        } catch (err) {
            console.error(err);
            alert("Failed ❌");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete =
        async () => {
            const confirmDelete =
                window.confirm(
                    "Delete this blog?"
                );

            if (!confirmDelete)
                return;

            await deleteDoc(
                doc(db, "blogs", id)
            );

            alert("Deleted ✅");

            navigate(
                "/dashboard/blog-json"
            );
        };

    const updateField = (
        key,
        value
    ) => {
        setBlog((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const updateArray = (
        field,
        index,
        value
    ) => {
        const updated = [
            ...(blog[field] || []),
        ];

        updated[index] = value;

        setBlog({
            ...blog,
            [field]: updated,
        });
    };

    const addArrayItem = (
        field
    ) => {
        setBlog({
            ...blog,
            [field]: [
                ...(blog[field] ||
                    []),
                "",
            ],
        });
    };

    const removeArrayItem = (
        field,
        index
    ) => {
        const updated =
            blog[field].filter(
                (_, i) =>
                    i !== index
            );

        setBlog({
            ...blog,
            [field]: updated,
        });
    };

    if (loading) {
        return (
            <div className="text-white">
                Loading...
            </div>
        );
    }

    return (
        <div className="text-white pb-40">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-orange-500">
                        {
                            blog.title
                        }
                    </h1>

                    <p className="text-slate-400">
                        Manage
                        Blog
                    </p>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() =>
                            navigate(
                                -1
                            )
                        }
                        className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl"
                    >
                        Back
                    </button>

                    <button
                        onClick={
                            handleDelete
                        }
                        className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl"
                    >
                        Delete
                    </button>
                </div>
            </div>

            {/* TABS */}
            <div className="flex flex-wrap gap-3 mb-8">
                {[
                    "overview",
                    "seo",
                    "media",
                    "content",
                    "cta",
                    "analytics",
                ].map(
                    (
                        tab
                    ) => (
                        <button
                            key={
                                tab
                            }
                            onClick={() =>
                                setActiveTab(
                                    tab
                                )
                            }
                            className={`px-4 py-2 rounded-xl capitalize transition-all ${activeTab ===
                                    tab
                                    ? "bg-orange-500"
                                    : "bg-white/10 hover:bg-white/20"
                                }`}
                        >
                            {
                                tab
                            }
                        </button>
                    )
                )}
            </div>

            {/* OVERVIEW */}
            {activeTab ===
                "overview" && (
                    <div className="grid md:grid-cols-2 gap-5">
                        <Input
                            label="Title"
                            value={
                                blog.title
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "title",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <Input
                            label="Slug"
                            value={
                                blog.slug
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "slug",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <Input
                            label="Category"
                            value={
                                blog.category
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "category",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <Input
                            label="Author"
                            value={
                                blog.author
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "author",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <div className="flex items-center gap-3 mt-8">
                            <input
                                type="checkbox"
                                checked={
                                    blog.featured
                                }
                                onChange={(
                                    e
                                ) =>
                                    updateField(
                                        "featured",
                                        e
                                            .target
                                            .checked
                                    )
                                }
                            />
                            <label>
                                Featured
                                Blog
                            </label>
                        </div>

                        <div className="md:col-span-2">
                            <Textarea
                                label="Description"
                                value={
                                    blog.description
                                }
                                onChange={(
                                    e
                                ) =>
                                    updateField(
                                        "description",
                                        e
                                            .target
                                            .value
                                    )
                                }
                            />
                        </div>
                    </div>
                )}

            {/* SEO */}
            {activeTab ===
                "seo" && (
                    <div className="space-y-5">
                        <Input
                            label="Meta Title"
                            value={
                                blog.metaTitle
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "metaTitle",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <Textarea
                            label="Meta Description"
                            value={
                                blog.metaDescription
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "metaDescription",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <ArrayEditor
                            title="Tags"
                            items={
                                blog.tags ||
                                []
                            }
                            onAdd={() =>
                                addArrayItem(
                                    "tags"
                                )
                            }
                            onChange={(
                                i,
                                v
                            ) =>
                                updateArray(
                                    "tags",
                                    i,
                                    v
                                )
                            }
                            onRemove={(
                                i
                            ) =>
                                removeArrayItem(
                                    "tags",
                                    i
                                )
                            }
                        />
                    </div>
                )}

            {/* MEDIA */}
            {activeTab ===
                "media" && (
                    <div className="space-y-5">
                        {blog.image}
                        <Input
                            label="Main Image"
                            value={
                                blog.image
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "image",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        {blog.image && (
                            <img
                                src={
                                    blog.image
                                }
                                className="rounded-xl max-h-72 object-cover"
                            />
                        )}

                        <Textarea
                            label="Gallery JSON"
                            value={JSON.stringify(
                                blog.gallery ||
                                [],
                                null,
                                2
                            )}
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "gallery",
                                    JSON.parse(
                                        e
                                            .target
                                            .value
                                    )
                                )
                            }
                        />
                    </div>
                )}

            {/* CONTENT */}
            {activeTab ===
                "content" && (
                    <Textarea
                        label="Blog Content"
                        rows={20}
                        value={
                            blog.content
                        }
                        onChange={(
                            e
                        ) =>
                            updateField(
                                "content",
                                e.target
                                    .value
                            )
                        }
                    />
                )}

            {/* CTA */}
            {activeTab ===
                "cta" && (
                    <div className="space-y-5">
                        <Input
                            label="CTA Text"
                            value={
                                blog.ctaText
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "ctaText",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />

                        <Input
                            label="CTA Link"
                            value={
                                blog.ctaLink
                            }
                            onChange={(
                                e
                            ) =>
                                updateField(
                                    "ctaLink",
                                    e
                                        .target
                                        .value
                                )
                            }
                        />
                    </div>
                )}

            {/* ANALYTICS */}
            {activeTab ===
                "analytics" && (
                    <div className="grid md:grid-cols-3 gap-5">
                        <StatCard
                            title="Views"
                            value={
                                blog.views
                            }
                        />
                        <StatCard
                            title="Likes"
                            value={
                                blog.likes
                            }
                        />
                        <StatCard
                            title="Reading Time"
                            value={`${blog.readingTime} min`}
                        />
                    </div>
                )}

            {/* SAVE */}
            <div className="fixed bottom-5 right-5 z-50">
                <button
                    onClick={
                        handleSave
                    }
                    disabled={
                        saving
                    }
                    className="bg-orange-500 hover:bg-orange-600 px-8 py-4 rounded-2xl shadow-2xl font-semibold"
                >
                    {saving
                        ? "Saving..."
                        : "Save Blog"}
                </button>
            </div>
        </div>
    );
}

/* COMPONENTS */

function Input({
    label,
    ...props
}) {
    return (
        <div>
            <label className="block mb-2 text-sm text-slate-300">
                {label}
            </label>

            <input
                {...props}
                className="w-full p-3 rounded-xl bg-black/40 border border-white/20"
            />
        </div>
    );
}

function Textarea({
    label,
    rows = 5,
    ...props
}) {
    return (
        <div>
            <label className="block mb-2 text-sm text-slate-300">
                {label}
            </label>

            <textarea
                rows={rows}
                {...props}
                className="w-full p-3 rounded-xl bg-black/40 border border-white/20"
            />
        </div>
    );
}

function ArrayEditor({
    title,
    items,
    onAdd,
    onChange,
    onRemove,
}) {
    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-orange-400">
                {title}
            </h2>

            {items.map(
                (
                    item,
                    index
                ) => (
                    <div
                        key={
                            index
                        }
                        className="flex gap-3"
                    >
                        <input
                            value={
                                item
                            }
                            onChange={(
                                e
                            ) =>
                                onChange(
                                    index,
                                    e
                                        .target
                                        .value
                                )
                            }
                            className="flex-1 p-3 rounded-xl bg-black/40 border border-white/20"
                        />

                        <button
                            onClick={() =>
                                onRemove(
                                    index
                                )
                            }
                            className="bg-red-600 px-4 rounded-xl"
                        >
                            Delete
                        </button>
                    </div>
                )
            )}

            <button
                onClick={
                    onAdd
                }
                className="bg-orange-500 px-5 py-3 rounded-xl"
            >
                Add
                {` ${title}`}
            </button>
        </div>
    );
}

function StatCard({
    title,
    value,
}) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-slate-400 text-sm">
                {title}
            </p>
            <h3 className="text-3xl font-bold text-orange-400 mt-2">
                {value}
            </h3>
        </div>
    );
}